import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_PhotoRecolor = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/photo-recolor',
			name: 'AI Recolor Tool – Change Colors in Photos Instantly | Lift',
			description:
				"Recolor any photo instantly with Lift's AI Recolor Tool. Change object, outfit, or product colors in seconds. No watermark — studio-quality results.",
			url: 'https://liftapp.ai/photo-recolor',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/photo-recolor#product' },
				{ '@id': 'https://liftapp.ai/photo-recolor#howto' },
				{ '@id': 'https://liftapp.ai/photo-recolor#benefits' },
				{ '@id': 'https://liftapp.ai/photo-recolor#faq' },
				{ '@id': 'https://liftapp.ai/photo-recolor#why-lift' },
				{ '@id': 'https://liftapp.ai/photo-recolor#explore-tools' },
				{ '@id': 'https://liftapp.ai/photo-recolor#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/photo-recolor#product',
			name: 'AI Recolor Tool',
			description:
				"Recolor any photo instantly with Lift's AI Recolor Tool. Change colors on clothes, products, or backgrounds in seconds with photorealistic blending.",
			url: 'https://liftapp.ai/photo-recolor',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/photo-recolor#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photo',
					text: "Add any product, portrait, or lifestyle image to Lift's editor.",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Select the area to recolor',
					text: 'Our AI understands fabrics, objects, and lighting — just highlight what you want to change.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Pick a new shade',
					text: 'Choose any color and the AI blends it seamlessly with highlights and shadows.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Export without watermark',
					text: "Preview, then download a studio-ready JPG or PNG when you're happy.",
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/photo-recolor#benefits',
			name: 'Who can benefit from AI Recolor Tool?',
			description:
				"Lift's AI Recolor Tool helps creators, sellers, and marketers test new color ideas without complex software.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Ideal for anyone who wants to experiment with color without learning complex photo editors',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Designers and marketers can quickly test new product colors or branding looks',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'E-commerce sellers can show the same product in multiple color options — instantly',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Photographers and creators can fine-tune tones to match their artistic vision',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'A must-have tool for stylists, decorators, and visual storytellers who care about perfect color harmony',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI Photo Recolor Tool by Lift',
				description:
					"Before and after example showing Lift's AI Recolor Tool applying a new palette to interior walls.",
			},
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/photo-recolor#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Recolor Tool?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Recolor Tool is an advanced color editing feature that lets you change any color in your photo — clothes, objects, or backgrounds — with AI precision.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Recolor Tool work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Our AI recognizes textures, materials, and light reflections. When you choose a new color, it automatically adjusts shadows and tones for a realistic, natural look.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will it look authentic?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Every recolor keeps the lighting and texture intact — so your edits look professional, not artificial.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/photo-recolor#why-lift',
			name: "Why Lift's AI Recolor Tool stands out",
			description:
				'Lift pairs pro-level AI recoloring with realistic lighting, platform flexibility, and affordable pricing.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Professional results',
					description:
						'Achieve studio-quality images without the need for complex tools',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI accuracy',
					description:
						'Smart algorithms deliver enhancements tailored to your specific image',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						"Save on costly editing services with Lift's affordable solution",
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift AI Recolor Tool',
				description:
					"Before and after photo of a coat showing how Lift's AI Recolor Tool keeps textures and lighting intact.",
			},
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/photo-recolor#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/photo-recolor#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Photo Recolor Tool',
					item: 'https://liftapp.ai/photo-recolor',
				},
			],
		},
	],
};
